import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, MessageCircle, Clock, Edit3 } from 'lucide-react';
import useWebSocket from '../../hooks/useWebSocket';
import { formatOrderRef } from '../../utils/orderRef';

const OrderDiscussion = ({ order, currentUserType = 'client', currentUserName = 'Dr. Martin Dubois', onSendMessage, onNewMessage }) => {
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [localMessages, setLocalMessages] = useState(order.discussion?.messages || []);
    const messagesContainerRef = useRef(null);
    const prevOrderIdRef = useRef(order.id);
    const prevMessagesRef = useRef(order.discussion?.messages);

    // Reset messages when viewing a different order OR when props messages changed
    useEffect(() => {
        const propsMessages = order.discussion?.messages || [];
        const orderChanged = prevOrderIdRef.current !== order.id;
        const messagesChanged = prevMessagesRef.current !== propsMessages;

        if (orderChanged) {
            // Nouvelle commande : réinitialiser complètement
            setLocalMessages(propsMessages);
            prevOrderIdRef.current = order.id;
            prevMessagesRef.current = propsMessages;
        } else if (messagesChanged && propsMessages.length > 0) {
            // Même commande mais les messages props ont changé
            // Fusionner avec les messages locaux pour ne pas perdre les messages WebSocket
            setLocalMessages(prev => {
                const existingIds = new Set(prev.map(m => m.id));
                const newFromProps = propsMessages.filter(m => !existingIds.has(m.id));
                if (newFromProps.length > 0) {
                    return [...prev, ...newFromProps];
                }
                return prev;
            });
            prevMessagesRef.current = propsMessages;
        }
    }, [order.id, order.discussion?.messages]);

    const scrollToBottom = () => {
        // Scroll only the chat container, not the parent modal
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [localMessages]);

    // Handle new message from WebSocket
    const handleWebSocketMessage = useCallback((message) => {
        // Check if message is for this order (if orderId is included)
        // Add to local messages if not already present
        setLocalMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === message.id)) {
                return prev;
            }
            return [...prev, message];
        });

        // Notify parent if callback provided
        if (onNewMessage) {
            onNewMessage(message);
        }
    }, [onNewMessage]);

    // Subscribe to WebSocket for this order
    useWebSocket({
        orderId: order.id,
        onNewMessage: handleWebSocketMessage
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || isLoading) return;

        setIsLoading(true);
        const messageData = {
            senderId: currentUserType === 'client' ? 'client_1' : 'prosthetist_1',
            senderName: currentUserName,
            senderType: currentUserType,
            message: newMessage.trim()
        };

        try {
            const sentMessage = await onSendMessage(order.id, messageData);
            setNewMessage('');

            // Add message to local state immediately (don't wait for WebSocket)
            if (sentMessage) {
                setLocalMessages(prev => {
                    // Avoid duplicates (in case WebSocket also delivers it)
                    if (prev.some(m => m.id === sentMessage.id)) {
                        return prev;
                    }
                    return [...prev, sentMessage];
                });
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi du message:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        
        if (isToday) {
            return date.toLocaleTimeString('fr-FR', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        } else {
            return date.toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit', 
                minute: '2-digit' 
            });
        }
    };

    // Deduplicate messages by ID (handles race conditions between useEffect and WebSocket)
    const messages = useMemo(() => {
        const seen = new Set();
        return localMessages.filter(m => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
    }, [localMessages]);
    const hasMessages = messages.length > 0;

    return (
        <div className="bg-black/30 border border-white/10 rounded-lg shadow-sm h-96 flex flex-col">
            {/* En-tête */}
            <div className="px-4 py-3 border-b border-white/10 glass rounded-t-lg">
                <div className="flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5 text-yellow-400" />
                    <h3 className="text-lg font-semibold text-white">
                        Discussion - Commande {formatOrderRef(order)}
                    </h3>
                </div>
                {order.discussion?.lastActivity && (
                    <div className="flex items-center space-x-1 mt-1 text-sm text-gray-400">
                        <Clock className="h-4 w-4" />
                        <span>Dernière activité: {formatTimestamp(order.discussion.lastActivity)}</span>
                    </div>
                )}
            </div>

            {/* Zone des messages */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-black/30">
                {!hasMessages ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <MessageCircle className="h-12 w-12 text-gray-600 mb-2" />
                        <p className="text-center">
                            Aucun message pour le moment.<br />
                            Commencez la discussion ci-dessous.
                        </p>
                    </div>
                ) : (
                    messages.map((message) => {
                        const isSystemMessage = message.senderType === 'system';
                        const isCurrentUser = message.senderType === currentUserType;

                        // System message rendering
                        if (isSystemMessage) {
                            return (
                                <div key={message.id} className="flex justify-center my-3">
                                    <div className="bg-white/10 border border-white/15 rounded-lg px-4 py-2 max-w-md">
                                        <div className="flex items-start space-x-2 text-gray-400">
                                            <Edit3 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm italic whitespace-pre-line">{message.message}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatTimestamp(message.timestamp)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        // Regular message rendering
                        return (
                            <div
                                key={message.id}
                                className={`flex animate-fade-in ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                        isCurrentUser
                                            ? 'bg-yellow-600 text-white'
                                            : 'bg-white/[0.07] border border-white/10 text-gray-200'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`text-xs font-medium ${
                                            isCurrentUser ? 'text-yellow-100' : 'text-gray-400'
                                        }`}>
                                            {message.senderName}
                                        </span>
                                        <span className={`text-xs ${
                                            isCurrentUser ? 'text-yellow-100' : 'text-gray-500'
                                        }`}>
                                            {formatTimestamp(message.timestamp)}
                                        </span>
                                    </div>
                                    <p className="text-sm">{message.message}</p>
                                    {!message.isRead && !isCurrentUser && (
                                        <div className="flex justify-end mt-1">
                                            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Zone de saisie */}
            <div className="border-t border-white/10 p-4">
                <form onSubmit={handleSubmit} className="flex space-x-2">
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Tapez votre message..."
                        className="flex-1 px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        disabled={isLoading}
                    />
                    <button
                        type="submit"
                        disabled={!newMessage.trim() || isLoading}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                    >
                        <Send className="h-4 w-4" />
                        <span className="hidden sm:inline">Envoyer</span>
                    </button>
                </form>
            </div>
        </div>
    );
};

export default OrderDiscussion;